import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Save, Plus, Trash2, Loader, Upload, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (url) => {
  if (!url) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

// ==== Reusable form components ====
export const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
      {label}
    </label>
    {children}
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

export const TextInput = ({ value, onChange, placeholder, testid, type = "text" }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
  />
);

export const TextArea = ({ value, onChange, placeholder, testid, rows = 3 }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    rows={rows}
    className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
  />
);

// Image URL input + optional file upload → base64 → /api/admin/uploads → returns /api/images/{id}
export const ImageInput = ({ value, onChange, token, testid }) => {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2_500_000) {
      setErr("Ukuran maksimal 2.5MB");
      return;
    }
    setErr("");
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await axios.post(
        `${API}/admin/uploads`,
        { filename: file.name, content_type: file.type, data_base64: b64 },
        { headers: { "X-Admin-Token": token } }
      );
      onChange(data.url);
    } catch (er) {
      setErr("Upload gagal");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-start">
        <input
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL atau upload foto"
          data-testid={testid}
          className="flex-1 border border-slate-300 rounded-xl px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
        />
        <label className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap ${uploading ? "bg-slate-200 text-slate-500" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
          {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Uploading..." : "Upload"}
          <input type="file" accept="image/*" onChange={onFile} className="hidden" disabled={uploading} />
        </label>
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      {value && (
        <div className="w-24 h-24 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={resolveImg(value)} alt="preview" className="w-full h-full object-cover" onError={(e) => (e.target.style.opacity = 0.3)} />
        </div>
      )}
    </div>
  );
};

// ==== Site Content Panel ====
export const SiteContentPanel = ({ token, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site-content`);
      setData(data);
    } catch {
      setErr("Gagal load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await axios.put(`${API}/admin/site-content`, data, {
        headers: { "X-Admin-Token": token },
      });
      setOk(true);
      setTimeout(() => setOk(false), 1800);
    } catch (e) {
      if (e.response?.status === 401) return onLogout();
      setErr("Gagal simpan");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data)
    return <div className="text-center py-16 text-slate-500">Memuat...</div>;

  return (
    <div className="space-y-6">
      {err && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-slate-900 mb-4">Brand & Kontak</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nama Brand (pendek)"><TextInput value={data.brand_name} onChange={set("brand_name")} testid="sc-brand-name" /></Field>
          <Field label="Nama Perusahaan Lengkap"><TextInput value={data.brand_full_name} onChange={set("brand_full_name")} testid="sc-brand-full" /></Field>
          <Field label="Tagline (di bawah logo)"><TextInput value={data.tagline} onChange={set("tagline")} testid="sc-tagline" /></Field>
          <Field label="WhatsApp (format 62xxx)" hint="Contoh: 6281234567890 (tanpa tanda +)"><TextInput value={data.whatsapp} onChange={set("whatsapp")} testid="sc-whatsapp" /></Field>
          <Field label="Email"><TextInput value={data.email} onChange={set("email")} testid="sc-email" /></Field>
          <Field label="Alamat"><TextInput value={data.address} onChange={set("address")} testid="sc-address" /></Field>
          <Field label="Jam Operasional"><TextInput value={data.hours} onChange={set("hours")} testid="sc-hours" /></Field>
          <Field label="Sertifikasi (footer)"><TextInput value={data.certification} onChange={set("certification")} testid="sc-cert" /></Field>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-slate-900 mb-4">Hero Section</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Kicker (atas headline)"><TextInput value={data.hero_kicker} onChange={set("hero_kicker")} testid="sc-hero-kicker" /></Field>
          <Field label="Headline utama"><TextInput value={data.hero_title} onChange={set("hero_title")} testid="sc-hero-title" /></Field>
          <Field label="Highlight (kata di-highlight hijau)"><TextInput value={data.hero_title_highlight} onChange={set("hero_title_highlight")} testid="sc-hero-hl" /></Field>
          <Field label="Akhiran headline"><TextInput value={data.hero_title_tail} onChange={set("hero_title_tail")} testid="sc-hero-tail" /></Field>
          <div className="sm:col-span-2">
            <Field label="Sub-headline"><TextArea value={data.hero_subtitle} onChange={set("hero_subtitle")} testid="sc-hero-sub" rows={2} /></Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Gambar Hero"><ImageInput value={data.hero_image} onChange={set("hero_image")} token={token} testid="sc-hero-img" /></Field>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-bold text-slate-900 mb-4">Trust Stats (3 badge di hero)</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="space-y-2">
              <Field label={`Nilai #${n}`}><TextInput value={data[`stat_${n}_value`]} onChange={set(`stat_${n}_value`)} testid={`sc-stat-${n}-value`} /></Field>
              <Field label={`Label #${n}`}><TextInput value={data[`stat_${n}_label`]} onChange={set(`stat_${n}_label`)} testid={`sc-stat-${n}-label`} /></Field>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        {ok && <span className="text-emerald-600 text-sm font-semibold inline-flex items-center gap-1" data-testid="sc-saved"><Check className="w-4 h-4" /> Tersimpan</span>}
        <button
          onClick={save}
          disabled={saving}
          data-testid="sc-save"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-6 py-3 rounded-full transition-colors"
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Simpan Perubahan
        </button>
      </div>
    </div>
  );
};

// ==== Generic CRUD panel ====
export const CrudPanel = ({
  token,
  onLogout,
  title,
  slug,
  publicSlug,
  emptyForm,
  renderRow,
  renderForm,
  keyLabel,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, ...form}
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const auth = { headers: { "X-Admin-Token": token } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const useAdmin = publicSlug.startsWith("admin/");
      const opts = useAdmin ? { headers: { "X-Admin-Token": token } } : {};
      const { data } = await axios.get(`${API}/${publicSlug}`, opts);
      setItems(data);
    } catch {
      setErr("Gagal load");
    } finally {
      setLoading(false);
    }
  }, [publicSlug, token]);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => setEditing({ ...emptyForm });
  const startEdit = (item) => setEditing({ ...item });

  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setErr("");
    try {
      const { id, ...payload } = editing;
      if (id) {
        await axios.patch(`${API}/admin/${slug}/${id}`, payload, auth);
      } else {
        await axios.post(`${API}/admin/${slug}`, payload, auth);
      }
      await load();
      setEditing(null);
    } catch (e) {
      if (e.response?.status === 401) return onLogout();
      setErr(e.response?.data?.detail || "Gagal simpan");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Yakin hapus item ini?")) return;
    try {
      await axios.delete(`${API}/admin/${slug}/${id}`, auth);
      await load();
    } catch (e) {
      if (e.response?.status === 401) return onLogout();
      setErr("Gagal hapus");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-slate-600 text-sm">{items.length} {keyLabel || "item"}</div>
        <button
          onClick={startAdd}
          data-testid={`${slug}-add`}
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2 rounded-full"
        >
          <Plus className="w-4 h-4" /> Tambah
        </button>
      </div>

      {err && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{err}</div>}

      {editing && (
        <div className="bg-white rounded-2xl border-2 border-emerald-400 p-6 shadow-lg" data-testid={`${slug}-editor`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">{editing.id ? `Edit ${title}` : `Tambah ${title}`}</h3>
            <button onClick={cancel} className="text-slate-400 hover:text-slate-700" aria-label="close">
              <X className="w-5 h-5" />
            </button>
          </div>
          {renderForm(editing, setEditing, token)}
          <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={cancel} className="text-slate-600 hover:text-slate-900 text-sm font-semibold px-4 py-2">
              Batal
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid={`${slug}-editor-save`}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-full"
            >
              {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Simpan
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-slate-500">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
          Belum ada data. Klik <b>Tambah</b> untuk menambahkan.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => (
            <div key={item.id} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 flex items-center gap-4 hover:border-emerald-500 transition-all" data-testid={`${slug}-row-${item.id}`}>
              <div className="flex-1 min-w-0">{renderRow(item)}</div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => startEdit(item)}
                  data-testid={`${slug}-edit-${item.id}`}
                  className="text-sm font-semibold text-emerald-700 hover:text-emerald-900 px-3 py-2"
                >
                  Edit
                </button>
                <button
                  onClick={() => del(item.id)}
                  data-testid={`${slug}-del-${item.id}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-800 px-3 py-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==== Content panels using CrudPanel ====
const SERVICE_ICONS = ["sprout", "mappin", "leaf", "eye", "plane", "wrench", "battery", "cpu", "sparkles"];

export const ServicesPanel = (props) => (
  <CrudPanel
    {...props}
    title="Layanan"
    slug="services"
    publicSlug="services"
    keyLabel="layanan"
    emptyForm={{ label: "", short: "", icon: "sprout", pricePerHa: 0, hoursPerHa: 0, savingsPct: 0, desc: "", order: 0 }}
    renderRow={(s) => (
      <div>
        <div className="font-bold text-slate-900">{s.label}</div>
        <div className="text-sm text-slate-500 mt-0.5">{s.desc}</div>
        <div className="text-xs text-emerald-700 font-semibold mt-1">
          Rp {Number(s.pricePerHa).toLocaleString("id-ID")}/ha · {s.hoursPerHa} jam/ha · Hemat {s.savingsPct}%
        </div>
      </div>
    )}
    renderForm={(v, set) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nama Layanan (Panjang)"><TextInput value={v.label} onChange={(x) => set({ ...v, label: x })} /></Field>
        <Field label="Nama Pendek (untuk kartu kalkulator)"><TextInput value={v.short} onChange={(x) => set({ ...v, short: x })} /></Field>
        <Field label="Icon">
          <select value={v.icon} onChange={(e) => set({ ...v, icon: e.target.value })} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white text-sm">
            {SERVICE_ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </Field>
        <Field label="Urutan (0 = pertama)"><TextInput type="number" value={v.order} onChange={(x) => set({ ...v, order: Number(x) })} /></Field>
        <Field label="Harga per Hektar (Rp)"><TextInput type="number" value={v.pricePerHa} onChange={(x) => set({ ...v, pricePerHa: Number(x) })} /></Field>
        <Field label="Jam per Hektar"><TextInput type="number" value={v.hoursPerHa} onChange={(x) => set({ ...v, hoursPerHa: Number(x) })} /></Field>
        <Field label="Estimasi Hemat (%)"><TextInput type="number" value={v.savingsPct} onChange={(x) => set({ ...v, savingsPct: Number(x) })} /></Field>
        <div className="sm:col-span-2">
          <Field label="Deskripsi"><TextArea value={v.desc} onChange={(x) => set({ ...v, desc: x })} rows={2} /></Field>
        </div>
      </div>
    )}
  />
);

export const PortfolioPanel = (props) => (
  <CrudPanel
    {...props}
    title="Portofolio"
    slug="portfolio"
    publicSlug="portfolio"
    keyLabel="case study"
    emptyForm={{ tag: "", title: "", metric: "", img: "", order: 0 }}
    renderRow={(p) => (
      <div className="flex items-center gap-3">
        {p.img && <img src={resolveImg(p.img)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />}
        <div className="min-w-0">
          <div className="font-bold text-slate-900 truncate">{p.title}</div>
          <div className="text-sm text-slate-500">{p.tag}</div>
          <div className="text-xs text-emerald-700 font-semibold mt-0.5">{p.metric}</div>
        </div>
      </div>
    )}
    renderForm={(v, set, token) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Tag Kategori"><TextInput value={v.tag} onChange={(x) => set({ ...v, tag: x })} /></Field>
        <Field label="Urutan"><TextInput type="number" value={v.order} onChange={(x) => set({ ...v, order: Number(x) })} /></Field>
        <div className="sm:col-span-2"><Field label="Judul"><TextInput value={v.title} onChange={(x) => set({ ...v, title: x })} /></Field></div>
        <div className="sm:col-span-2"><Field label="Metrik / Hasil"><TextInput value={v.metric} onChange={(x) => set({ ...v, metric: x })} /></Field></div>
        <div className="sm:col-span-2"><Field label="Gambar"><ImageInput value={v.img} onChange={(x) => set({ ...v, img: x })} token={token} /></Field></div>
      </div>
    )}
  />
);

export const TestimonialsPanel = (props) => (
  <CrudPanel
    {...props}
    title="Testimoni"
    slug="testimonials"
    publicSlug="testimonials"
    keyLabel="testimoni"
    emptyForm={{ name: "", role: "", body: "", order: 0 }}
    renderRow={(t) => (
      <div>
        <div className="font-bold text-slate-900">{t.name}</div>
        <div className="text-sm text-slate-500">{t.role}</div>
        <div className="text-sm text-slate-600 mt-1 line-clamp-2">&ldquo;{t.body}&rdquo;</div>
      </div>
    )}
    renderForm={(v, set) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nama"><TextInput value={v.name} onChange={(x) => set({ ...v, name: x })} /></Field>
        <Field label="Peran / Posisi"><TextInput value={v.role} onChange={(x) => set({ ...v, role: x })} /></Field>
        <Field label="Urutan"><TextInput type="number" value={v.order} onChange={(x) => set({ ...v, order: Number(x) })} /></Field>
        <div className="sm:col-span-2"><Field label="Isi Testimoni"><TextArea value={v.body} onChange={(x) => set({ ...v, body: x })} rows={3} /></Field></div>
      </div>
    )}
  />
);

export const FaqPanel = (props) => (
  <CrudPanel
    {...props}
    title="FAQ"
    slug="faqs"
    publicSlug="faqs"
    keyLabel="pertanyaan"
    emptyForm={{ q: "", a: "", order: 0 }}
    renderRow={(f) => (
      <div>
        <div className="font-bold text-slate-900">{f.q}</div>
        <div className="text-sm text-slate-500 mt-1 line-clamp-2">{f.a}</div>
      </div>
    )}
    renderForm={(v, set) => (
      <div className="space-y-4">
        <Field label="Pertanyaan"><TextInput value={v.q} onChange={(x) => set({ ...v, q: x })} /></Field>
        <Field label="Jawaban"><TextArea value={v.a} onChange={(x) => set({ ...v, a: x })} rows={4} /></Field>
        <Field label="Urutan"><TextInput type="number" value={v.order} onChange={(x) => set({ ...v, order: Number(x) })} /></Field>
      </div>
    )}
  />
);

const CATEGORIES = [
  { id: "drone", label: "Drone" },
  { id: "sparepart", label: "Sparepart" },
  { id: "aksesoris", label: "Aksesoris" },
];
const STOCK_OPTIONS = [
  { id: "ready", label: "Ready Stock" },
  { id: "preorder", label: "Pre-Order" },
  { id: "soldout", label: "Sold Out" },
];

export const ProductsFullPanel = (props) => (
  <CrudPanel
    {...props}
    title="Produk"
    slug="products"
    publicSlug="products"
    keyLabel="produk"
    emptyForm={{ name: "", category: "drone", tagline: "", price: 0, priceLabel: "", badge: "", specs: [], img: "", accent: "from-emerald-600 to-emerald-900", stock: "ready" }}
    renderRow={(p) => (
      <div className="flex items-center gap-3">
        {p.img ? (
          <img src={resolveImg(p.img)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-slate-100" />
        ) : (
          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${p.accent || "from-emerald-600 to-emerald-900"} flex-shrink-0`} />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-slate-900">{p.name}</div>
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold uppercase tracking-wider">
              {p.category}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white ${p.stock === "ready" ? "bg-emerald-500" : p.stock === "preorder" ? "bg-amber-500" : "bg-slate-800"}`}>
              {p.stock}
            </span>
          </div>
          <div className="text-sm text-slate-500 truncate mt-0.5">{p.tagline}</div>
          <div className="text-emerald-700 font-extrabold text-sm mt-1">{p.priceLabel}</div>
        </div>
      </div>
    )}
    renderForm={(v, set, token) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nama Produk"><TextInput value={v.name} onChange={(x) => set({ ...v, name: x })} /></Field>
        <Field label="Kategori">
          <select value={v.category} onChange={(e) => set({ ...v, category: e.target.value })} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white text-sm">
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2"><Field label="Tagline (deskripsi singkat)"><TextInput value={v.tagline} onChange={(x) => set({ ...v, tagline: x })} /></Field></div>
        <Field label="Harga Angka (Rp)"><TextInput type="number" value={v.price} onChange={(x) => set({ ...v, price: Number(x) })} /></Field>
        <Field label="Label Harga (tampilan)" hint="Contoh: Rp 285 jt"><TextInput value={v.priceLabel} onChange={(x) => set({ ...v, priceLabel: x })} /></Field>
        <Field label="Badge (opsional)" hint="Contoh: Best Seller, Populer"><TextInput value={v.badge || ""} onChange={(x) => set({ ...v, badge: x })} /></Field>
        <Field label="Status Stok">
          <select value={v.stock} onChange={(e) => set({ ...v, stock: e.target.value })} className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white text-sm">
            {STOCK_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Spesifikasi (1 baris = 1 spec)">
            <TextArea value={(v.specs || []).join("\n")} onChange={(x) => set({ ...v, specs: x.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={3} />
          </Field>
        </div>
        <div className="sm:col-span-2"><Field label="Gambar Produk (opsional)" hint="Kalau kosong, kartu pakai gradient warna"><ImageInput value={v.img} onChange={(x) => set({ ...v, img: x })} token={token} /></Field></div>
        <div className="sm:col-span-2"><Field label="Accent Gradient (kalau tanpa gambar)" hint="Contoh: from-emerald-600 to-emerald-900"><TextInput value={v.accent || ""} onChange={(x) => set({ ...v, accent: x })} /></Field></div>
      </div>
    )}
  />
);

export const BundlesPanel = (props) => (
  <CrudPanel
    {...props}
    title="Bundle"
    slug="bundles"
    publicSlug="admin/bundles"
    keyLabel="paket bundling"
    emptyForm={{
      name: "",
      tagline: "",
      product_ids: [],
      original_price: 0,
      price: 0,
      priceLabel: "",
      originalPriceLabel: "",
      discount_pct: 0,
      image: "",
      accent: "from-emerald-600 to-emerald-900",
      badge: "",
      highlights: [],
      order: 0,
      active: true,
    }}
    renderRow={(b) => (
      <div className="flex items-center gap-3">
        {b.image ? (
          <img src={resolveImg(b.image)} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-slate-100" />
        ) : (
          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${b.accent || "from-emerald-600 to-emerald-900"} flex-shrink-0`} />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-bold text-slate-900">{b.name}</div>
            {b.badge && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">{b.badge}</span>}
            {!b.active && <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wider">Nonaktif</span>}
          </div>
          <div className="text-sm text-slate-500 truncate mt-0.5">{b.tagline}</div>
          <div className="text-xs mt-1">
            <span className="text-emerald-700 font-extrabold">{b.priceLabel}</span>
            <span className="text-slate-400 line-through ml-2">{b.originalPriceLabel}</span>
            <span className="text-amber-700 ml-2 font-bold">-{Math.round(b.discount_pct)}%</span>
          </div>
        </div>
      </div>
    )}
    renderForm={(v, set, token) => (
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Field label="Nama Bundle"><TextInput value={v.name} onChange={(x) => set({ ...v, name: x })} /></Field></div>
        <div className="sm:col-span-2"><Field label="Tagline"><TextInput value={v.tagline} onChange={(x) => set({ ...v, tagline: x })} /></Field></div>
        <Field label="Harga Bundle (Rp)"><TextInput type="number" value={v.price} onChange={(x) => set({ ...v, price: Number(x) })} /></Field>
        <Field label="Harga Asli (Rp)"><TextInput type="number" value={v.original_price} onChange={(x) => set({ ...v, original_price: Number(x) })} /></Field>
        <Field label="Label Harga Bundle" hint="Contoh: Rp 199 jt"><TextInput value={v.priceLabel} onChange={(x) => set({ ...v, priceLabel: x })} /></Field>
        <Field label="Label Harga Asli"><TextInput value={v.originalPriceLabel} onChange={(x) => set({ ...v, originalPriceLabel: x })} /></Field>
        <Field label="Persen Diskon"><TextInput type="number" value={v.discount_pct} onChange={(x) => set({ ...v, discount_pct: Number(x) })} /></Field>
        <Field label="Badge (opsional)" hint="Best Value, Recommended"><TextInput value={v.badge || ""} onChange={(x) => set({ ...v, badge: x })} /></Field>
        <Field label="ID Produk yang termasuk" hint="Pisahkan koma, contoh: dji-agras-t50,rtk-base">
          <TextInput
            value={(v.product_ids || []).join(",")}
            onChange={(x) => set({ ...v, product_ids: x.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
        </Field>
        <Field label="Urutan"><TextInput type="number" value={v.order} onChange={(x) => set({ ...v, order: Number(x) })} /></Field>
        <div className="sm:col-span-2">
          <Field label="Highlights (1 baris = 1 poin)">
            <TextArea
              value={(v.highlights || []).join("\n")}
              onChange={(x) => set({ ...v, highlights: x.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={3}
            />
          </Field>
        </div>
        <div className="sm:col-span-2"><Field label="Gambar Bundle"><ImageInput value={v.image} onChange={(x) => set({ ...v, image: x })} token={token} /></Field></div>
        <Field label="Accent Gradient"><TextInput value={v.accent || ""} onChange={(x) => set({ ...v, accent: x })} /></Field>
        <Field label="Status">
          <select
            value={v.active ? "1" : "0"}
            onChange={(e) => set({ ...v, active: e.target.value === "1" })}
            className="w-full border border-slate-300 rounded-xl px-3 py-2.5 bg-white text-sm"
          >
            <option value="1">Aktif (tampil di landing)</option>
            <option value="0">Nonaktif (disembunyikan)</option>
          </select>
        </Field>
      </div>
    )}
  />
);
